import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { namespace } from "@common-config/constants";
import type {
  McpLimitWindow,
  McpToolLimit,
  McpToolPolicy,
} from "@common/reducers/backend-api/backend-api";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/** BE#1317: per-tool "never" and per-contact cap on one tool of a customer-attached MCP server. */
const WINDOWS: McpLimitWindow[] = ["day", "week", "month", "lifetime"];
const DEFAULT_LIMIT = { max: 3, window: "week" as McpLimitWindow };

/** Same rule as the backend `matchesDeny`: an exact name, or a prefix ending in one `*`. */
export const matchesPattern = (pattern: string, name: string): boolean =>
  pattern.endsWith("*")
    ? name.startsWith(pattern.slice(0, -1))
    : pattern === name;

interface Props {
  /** Scopes the element ids: two servers can expose a tool of the same name. */
  serverId: string;
  toolName: string;
  enabledTool: boolean;
  policy?: McpToolPolicy;
  available: { deny: boolean; limits: boolean };
  busy: boolean;
  classes: { hint: string; input: string };
  onChange: (next: {
    deny?: string[];
    limits?: McpToolLimit[];
  }) => Promise<unknown> | void;
}

export const ToolPolicyControls: React.FC<Props> = ({
  serverId,
  toolName,
  enabledTool,
  policy,
  available,
  busy,
  classes,
  onChange,
}) => {
  const { t } = useTranslation(namespace);
  const deny = policy?.deny ?? [];
  const limits = policy?.limits ?? [];
  const blockedBy = deny.find((p) => matchesPattern(p, toolName));
  const own = limits.find((l) => l.tool === toolName);
  const coveredBy = own
    ? undefined
    : limits.find((l) => matchesPattern(l.tool, toolName));
  const [max, setMax] = useState(String(own?.max ?? DEFAULT_LIMIT.max));
  useEffect(() => setMax(String(own?.max ?? DEFAULT_LIMIT.max)), [own?.max]);

  const setLimit = (next: McpToolLimit | null) =>
    onChange({
      limits: [
        ...limits.filter((l) => l.tool !== toolName),
        ...(next ? [next] : []),
      ],
    });
  const commitMax = () => {
    const n = Number(max);
    if (!own || !Number.isInteger(n) || n < 1 || n > 1000)
      return setMax(String(own?.max ?? DEFAULT_LIMIT.max));
    if (n !== own.max) void setLimit({ ...own, max: n });
  };

  return (
    <div
      className="mt-1 space-y-1"
      data-testid={`agent-mcp-policy-${toolName}`}
    >
      {available.deny && (
        <div className="flex items-center gap-1.5">
          <Checkbox
            id={`mcp-deny-${serverId}-${toolName}`}
            checked={!!blockedBy}
            disabled={busy || (!!blockedBy && blockedBy !== toolName)}
            onCheckedChange={(v) =>
              onChange({
                deny:
                  v === true
                    ? [...deny, toolName]
                    : deny.filter((p) => p !== toolName),
              })
            }
            data-testid={`agent-mcp-deny-${toolName}`}
          />
          <label
            htmlFor={`mcp-deny-${serverId}-${toolName}`}
            className={`cursor-pointer ${classes.hint}`}
          >
            {blockedBy && blockedBy !== toolName
              ? t("myBridge.mcp.policy.blockedByRule", { rule: blockedBy })
              : t("myBridge.mcp.policy.block")}
          </label>
        </div>
      )}
      {available.limits && enabledTool && !blockedBy && (
        <div className="flex flex-wrap items-center gap-1.5">
          {coveredBy ? (
            <span className={classes.hint}>
              {t("myBridge.mcp.policy.limitByRule", { rule: coveredBy.tool })}
            </span>
          ) : (
            <>
              <Checkbox
                id={`mcp-limit-${serverId}-${toolName}`}
                checked={!!own}
                disabled={busy}
                onCheckedChange={(v) =>
                  setLimit(
                    v === true ? { tool: toolName, ...DEFAULT_LIMIT } : null
                  )
                }
                data-testid={`agent-mcp-limit-${toolName}`}
              />
              <label
                htmlFor={`mcp-limit-${serverId}-${toolName}`}
                className={`cursor-pointer ${classes.hint}`}
              >
                {t("myBridge.mcp.policy.limit")}
              </label>
              {own && (
                <>
                  <Input
                    type="number"
                    min={1}
                    max={1000}
                    value={max}
                    disabled={busy}
                    onChange={(e) => setMax(e.target.value)}
                    onBlur={commitMax}
                    aria-label={t("myBridge.mcp.policy.max")}
                    className={`${classes.input} h-7 w-16`}
                    data-testid={`agent-mcp-limit-max-${toolName}`}
                  />
                  <select
                    value={own.window}
                    disabled={busy}
                    onChange={(e) =>
                      void setLimit({
                        ...own,
                        window: e.target.value as McpLimitWindow,
                      })
                    }
                    aria-label={t("myBridge.mcp.policy.window")}
                    className="h-7 rounded-md border border-ai-border bg-transparent px-1 text-xs text-ai-text"
                    data-testid={`agent-mcp-limit-window-${toolName}`}
                  >
                    {WINDOWS.map((w) => (
                      <option key={w} value={w}>
                        {t(`myBridge.mcp.policy.windows.${w}`)}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
